// frontend/src/screens/RegisterScreen.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { REST_API_ORIGIN } from '../platform/endpoints';
import { logOperationalError } from '../utils/safeLogger';

const API_BASE = REST_API_ORIGIN;

const RegisterScreen = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    birthdate: '',
    gender: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    setSuccess('');
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Valida fecha ISO (yyyy-mm-dd) para TODOS los dispositivos
  const normalizeBirthdate = (raw) => {
    if (!raw) return null;
    const isoRe = /^(\d{4})-(\d{2})-(\d{2})$/;
    const m = raw.match(isoRe);
    if (!m) return null;
    const [, yyyy, mm, dd] = m;
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00Z`);
    if (
      Number.isNaN(d.getTime()) ||
      d.getUTCFullYear().toString() !== yyyy ||
      String(d.getUTCMonth() + 1).padStart(2, '0') !== mm ||
      String(d.getUTCDate()).padStart(2, '0') !== dd
    ) {
      return null;
    }
    const min = new Date('1900-01-01T00:00:00Z').getTime();
    const today = new Date();
    const end = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)).getTime();
    const ts = d.getTime();
    if (ts < min || ts > end) return null;
    return raw; // ISO ok
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (
      !formData.name ||
      !formData.email ||
      !formData.birthdate ||
      !formData.gender ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      setError(t('register.errors.requiredFields'));
      return;
    }

    const emailNormalized = formData.email.trim().toLowerCase();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailNormalized)) {
      setError(t('register.errors.invalidEmail'));
      return;
    }

    const passwordStrong = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;
    if (!passwordStrong.test(formData.password)) {
      setError(t('register.errors.weakPassword'));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return;
    }

    const dobIso = normalizeBirthdate(formData.birthdate.trim());
    if (!dobIso) {
      setError(t('register.errors.invalidBirthdate'));
      return;
    }

    try {
      setLoading(true);
      const formPayload = new FormData();
      formPayload.append('fullName', formData.name.trim());
      formPayload.append('email', emailNormalized);
      formPayload.append('dob', dobIso);
      formPayload.append('gender', formData.gender);
      formPayload.append('password', formData.password);

      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        body: formPayload,
        credentials: 'include',
      });

      let data = null;
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : null;
      } catch (_) {
        data = null;
      }

      if (!res.ok) {
        if (res.status === 409) {
          setError(t('register.errors.emailExists'));
          return;
        }
       const msg =
  (data && (data.message || data.error)) ||
  t('register.errors.registerFailed');
        setError(msg);
        return;
      }

     setSuccess(t('register.success.verifyEmail'));
      setTimeout(() => {
        navigate('/login', { replace: true });
      }, 1200);
    } catch (err) {
      logOperationalError('auth.register', { status: err?.status, code: 'request_failed' });
      setError(t('register.errors.connection'));
    } finally {
      setLoading(false);
    }
  };

return (
    <div id="registerBgHost" style={styles.root}>
      <style>{`
        #registerBgHost::before {
          content: "";
          position: fixed;
          inset: -20%;
          z-index: 0;
          pointer-events: none;
          background:
            radial-gradient(circle at 18% 18%, rgba(177,74,255,0.24) 0%, transparent 25%),
            radial-gradient(circle at 84% 58%, rgba(123,31,205,0.22) 0%, transparent 31%),
            radial-gradient(circle at 50% 106%, rgba(218,165,32,0.18) 0%, transparent 30%);
          filter: blur(2px);
        }

        #registerBgHost::after {
          content: "";
          position: fixed;
          inset: 0;
          z-index: 1;
          pointer-events: none;
          background-image:
            radial-gradient(circle, rgba(255,226,138,0.88) 0 1px, transparent 1.45px),
            radial-gradient(circle, rgba(255,255,255,0.58) 0 1px, transparent 1.35px),
            linear-gradient(180deg, rgba(0,0,0,0.04), rgba(0,0,0,0.72));
          background-size: 112px 112px, 174px 174px, 100% 100%;
          background-position: 22px 18px, 86px 44px, center;
          opacity: 0.58;
          animation: register-star-drift 18s linear infinite;
        }

        @keyframes register-star-drift {
          from { background-position: 22px 18px, 86px 44px, center; }
          to { background-position: 22px 130px, 86px 218px, center; }
        }

        @keyframes register-card-wheel-spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          #registerBgHost::after,
          #registerCard::before {
            animation: none !important;
          }
        }

        #registerCard {
          overflow: hidden;
        }

        #registerCard::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: min(122vw, 540px);
          height: min(122vw, 540px);
          z-index: 0;
          pointer-events: none;
          border-radius: 50%;
          overflow: hidden;
          background-image: url("/logo/Zodiacus.jpeg");
          background-repeat: no-repeat;
          background-position: center center;
          background-size: cover;
          opacity: 0.34;
          mix-blend-mode: screen;
          filter: brightness(1.75) contrast(1.08) saturate(1.2) drop-shadow(0 0 30px rgba(218,165,32,0.34)) drop-shadow(0 0 46px rgba(154,47,255,0.26));
          animation: register-card-wheel-spin 155s linear infinite;
          transform: translate(-50%, -50%) rotate(0deg);
          transform-origin: center center;
        }

        #registerCard > * {
          position: relative;
          z-index: 2;
        }

        #registerCard input::placeholder {
          color: rgba(255,232,168,0.36);
        }

        #registerCard select option {
          color: #1E1230;
          background: #FFF6D8;
        }

        @media (max-width: 540px) {
          #registerBackCircle {
            top: calc(-4px + env(safe-area-inset-top, 0px)) !important;
          }

          #registerLangTop {
            top: calc(-10px + env(safe-area-inset-top, 0px)) !important;
            right: calc(10px + env(safe-area-inset-right, 0px)) !important;
            padding: 0.42rem 0.64rem !important;
            gap: 5px !important;
            transform: scale(0.86);
            transform-origin: top right;
          }

          #registerContent {
            min-height: 100svh !important;
            padding: 72px 20px 24px !important;
            justify-content: center !important;
            animation: none !important;
            transform: translateY(-12px) !important;
          }

          #registerBackCircle {
            width: 50px !important;
            height: 50px !important;
            font-size: 27px !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            padding: 0 !important;
            line-height: 1 !important;
          }

          #registerCard {
            padding: 24px 20px 22px !important;
            border-radius: 24px !important;
          }

          #registerCard h2 {
            font-size: clamp(1.48rem, 6.65vw, 2.02rem) !important;
            letter-spacing: 0.01em !important;
            max-width: calc(100% - 86px);
            margin-left: auto !important;
            margin-right: auto !important;
            line-height: 1.05 !important;
            text-align: center !important;
          }

          #registerCard::before {
            width: min(116vw, 420px);
            height: min(116vw, 420px);
            opacity: 0.36;
          }

          #registerCard [style*="grid-template-columns"] {
            grid-template-columns: 22px minmax(0, 1fr) !important;
            gap: 8px !important;
          }
        }
      `}</style>

      <button id="registerBackCircle" type="button" aria-label={t('register.back')} onClick={() => navigate('/welcome', { replace: true })} style={styles.backCircle}>
        <span aria-hidden="true" style={styles.backArrow}>←</span>
      </button>

      <div id="registerLangTop" style={styles.langTop} aria-label={t('welcome.languageSelector')}>
        <span style={styles.langIcon}>◎</span>
        <button
          type="button"
          onClick={() => i18n.changeLanguage('es')}
          style={{ ...styles.langTopButton, opacity: i18n.language?.startsWith('es') ? 1 : 0.55 }}
        >
          ES
        </button>
        <span style={styles.langDivider}>/</span>
        <button
          type="button"
          onClick={() => i18n.changeLanguage('en')}
          style={{ ...styles.langTopButton, opacity: i18n.language?.startsWith('en') ? 1 : 0.55 }}
        >
          EN
        </button>
      </div>

      <main id="registerContent" style={styles.container}>
        <form id="registerCard" onSubmit={handleSubmit} style={styles.form} encType="multipart/form-data" autoComplete="on">
          <div style={styles.formHeader}>
            <h2 style={styles.title}>{t('register.title')}</h2>
            <p style={styles.formSubtitle}>
              {t(
                'register.subtitle',
                i18n.language?.startsWith('en')
                  ? 'Create your account to enter Zodiacus'
                  : 'Crea tu cuenta para entrar a Zodiacus'
              )}
            </p>
          </div>

          {error && <div style={styles.error} role="alert" aria-live="assertive">{error}</div>}
          {success && <div style={styles.success} role="status" aria-live="polite">{success}</div>}

          <div style={styles.fieldRow}>
            <span aria-hidden="true" style={styles.fieldIcon}>♙</span>
            <div style={styles.fieldContent}>
              <label htmlFor="regName" style={styles.label}>{t('register.nameLabel')}</label>
              <input
                id="regName"
                name="name"
                type="text"
                placeholder={t('register.namePlaceholder')}
                value={formData.name}
                onChange={handleChange}
                style={styles.input}
                autoComplete="name"
                autoCapitalize="words"
                autoCorrect="off"
                required
              />
            </div>
          </div>

          <div style={styles.fieldRow}>
            <span aria-hidden="true" style={styles.fieldIcon}>✉</span>
            <div style={styles.fieldContent}>
              <label htmlFor="regEmail" style={styles.label}>{t('register.emailLabel')}</label>
              <input
                id="regEmail"
                name="email"
                type="email"
                placeholder={t('register.emailPlaceholder')}
                value={formData.email}
                onChange={handleChange}
                style={styles.input}
                autoComplete="email"
                inputMode="email"
                required
              />
            </div>
          </div>

          <div style={styles.fieldRow}>
            <span aria-hidden="true" style={styles.fieldIcon}>☽</span>
            <div style={styles.fieldContent}>
              <label htmlFor="regBirthdate" style={styles.label}>{t('register.birthdateLabel')}</label>
              <input
                id="regBirthdate"
                name="birthdate"
                type="date"
                value={formData.birthdate}
                onChange={handleChange}
                style={styles.input}
                min="1900-01-01"
                max={new Date().toISOString().split('T')[0]}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && typeof e.currentTarget.showPicker === 'function') {
                    e.preventDefault();
                    e.currentTarget.showPicker();
                  }
                }}
                required
              />
            </div>
          </div>

          <div style={styles.fieldRow}>
            <span aria-hidden="true" style={styles.fieldIcon}>◇</span>
            <div style={styles.fieldContent}>
              <label htmlFor="regGender" style={styles.label}>{t('register.genderLabel')}</label>
              <select
                id="regGender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                style={styles.input}
                required
              >
                <option value="" disabled>{t('register.genderPlaceholder')}</option>
                <option value="Hombre">{t('register.genderOptions.male')}</option>
                <option value="Mujer">{t('register.genderOptions.female')}</option>
                <option value="Otro">{t('register.genderOptions.other')}</option>
              </select>
            </div>
          </div>

          <div style={styles.fieldRow}>
            <span aria-hidden="true" style={styles.fieldIcon}>▣</span>
            <div style={styles.fieldContent}>
              <label htmlFor="regPassword" style={styles.label}>{t('register.passwordLabel')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="regPassword"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('register.passwordPlaceholder')}
                  value={formData.password}
                  onChange={handleChange}
                  style={{ ...styles.input, paddingRight: 52 }}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={styles.toggleIcon}
                  aria-label={showPassword ? t('register.hidePassword') : t('register.showPassword')}
                >
                  {showPassword ? '🙈' : '◉'}
                </button>
              </div>
            </div>
          </div>

          <div style={styles.fieldRow}>
            <span aria-hidden="true" style={styles.fieldIcon}>▣</span>
            <div style={styles.fieldContent}>
              <label htmlFor="regConfirmPassword" style={styles.label}>{t('register.confirmPasswordLabel')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="regConfirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder={t('register.confirmPasswordPlaceholder')}
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  style={{ ...styles.input, paddingRight: 52 }}
                  autoComplete="new-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={styles.toggleIcon}
                  aria-label={showConfirmPassword ? t('register.hideConfirmPassword') : t('register.showConfirmPassword')}
                >
                  {showConfirmPassword ? '🙈' : '◉'}
                </button>
              </div>
              {formData.confirmPassword && formData.confirmPassword !== formData.password && (
                <small style={styles.smallError}>{t('register.errors.passwordMismatch')}</small>
              )}
            </div>
          </div>

          <div style={styles.securityNote}>
            <span aria-hidden="true">🛡️</span>
            <span>{t('register.securityNote')}</span>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{ ...styles.button, opacity: loading ? 0.66 : 1 }}
          >
            <span aria-hidden="true" style={styles.btnSpark}>✦</span>
            {loading ? t('register.loading') : t('register.submit')}
            <span aria-hidden="true" style={styles.btnSpark}>✦</span>
          </button>
        </form>

        <div style={styles.loginBox}>
          <div style={styles.dividerRow}>
            <span style={styles.dividerLine} />
            <span style={styles.dividerText}>{t('register.haveAccount')}</span>
            <span style={styles.dividerLine} />
          </div>
          <Link to="/login" style={styles.loginButton}>{t('register.loginHere')}</Link>
        </div>

        <div aria-hidden="true" style={styles.bottomSigil}>
          <span />
          <i>✦</i>
          <span />
        </div>
      </main>
    </div>
  );
};

const styles = {
  root: {
    minHeight: '100vh',
    position: 'relative',
    isolation: 'isolate',
    overflow: 'hidden',
    color: '#fff',
    background:
      'radial-gradient(circle at 50% 30%, rgba(110,24,184,0.32) 0%, rgba(30,0,70,0.48) 32%, rgba(4,1,15,0.98) 74%), linear-gradient(180deg, #05010F 0%, #150028 50%, #04000D 100%)',
  },
  backCircle: {
    position: 'fixed',
    top: 'calc(-2px + env(safe-area-inset-top, 0px))',
    left: 'calc(22px + env(safe-area-inset-left, 0px))',
    zIndex: 250,
    width: 54,
    height: 54,
    borderRadius: '50%',
    border: '1px solid rgba(255,218,116,0.78)',
    background: 'rgba(8,2,24,0.54)',
    color: '#FFD979',
    fontSize: 28,
    lineHeight: 1,
    cursor: 'pointer',
    boxShadow: '0 0 24px rgba(218,165,32,0.22), inset 0 0 18px rgba(218,165,32,0.05)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    boxSizing: 'border-box',
    overflow: 'hidden',
  },
  backArrow: {
    display: 'block',
    lineHeight: 1,
    transform: 'translateY(-1px)',
  },
  langTop: {
    position: 'fixed',
    top: 'calc(-8px + env(safe-area-inset-top, 0px))',
    right: 'calc(12px + env(safe-area-inset-right, 0px))',
    zIndex: 250,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '0.62rem 0.9rem',
    borderRadius: '999px',
    border: '1px solid rgba(232,95,255,0.72)',
    background: 'rgba(14,4,31,0.72)',
    color: '#FFF0B8',
    boxShadow: '0 0 22px rgba(190,60,255,0.34), inset 0 0 18px rgba(218,165,32,0.08)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
  },
  langIcon: {
    fontSize: '1.1rem',
    color: '#FFE8A8',
    lineHeight: 1,
  },
  langDivider: {
    color: 'rgba(255,232,168,0.45)',
    fontWeight: 800,
  },
  langTopButton: {
    border: 0,
    background: 'transparent',
    color: '#FFF0B8',
    fontWeight: 900,
    letterSpacing: '0.06em',
    fontSize: '0.95rem',
    cursor: 'pointer',
    padding: 0,
  },
  container: {
    position: 'relative',
    zIndex: 5,
    minHeight: '100svh',
    width: '100%',
    maxWidth: 760,
    margin: '0 auto',
    padding: '72px 26px 28px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  form: {
    position: 'relative',
    zIndex: 4,
    width: '100%',
    maxWidth: 520,
    transform: 'translateY(-18px)',
    borderRadius: 32,
    border: '1px solid rgba(232,95,255,0.44)',
    background: 'linear-gradient(180deg, rgba(20,5,42,0.26), rgba(8,2,24,0.32))',
    boxShadow: '0 24px 70px rgba(0,0,0,0.48), 0 0 32px rgba(171,54,255,0.20), inset 0 1px 0 rgba(255,255,255,0.06)',
    color: '#FFF6D8',
    padding: '30px 34px 28px',
    boxSizing: 'border-box',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
  },
  formHeader: {
    textAlign: 'center',
    marginBottom: 24,
  },
  title: {
    color: '#FFD979',
    margin: 0,
    fontWeight: 500,
    fontSize: 'clamp(1.62rem, 5.2vw, 2.35rem)',
    fontFamily: "'Cinzel', 'Cormorant Garamond', serif",
    textShadow: '0 0 18px rgba(218,165,32,0.18)',
  },
  formSubtitle: {
    margin: '8px 0 0',
    color: 'rgba(255,255,255,0.72)',
    fontSize: 'clamp(0.96rem, 3vw, 1.1rem)',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '26px minmax(0, 1fr)',
    gap: 10,
    alignItems: 'end',
    marginTop: 14,
  },
  fieldIcon: {
    color: '#FFD979',
    fontSize: 24,
    lineHeight: '52px',
    textAlign: 'center',
    textShadow: '0 0 12px rgba(218,165,32,0.35)',
  },
  fieldContent: {
    minWidth: 0,
  },
  label: {
    display: 'block',
    marginBottom: 8,
    color: 'rgba(255,255,255,0.92)',
    fontWeight: 700,
    fontSize: '0.95rem',
  },
  input: {
    width: '100%',
    height: 54,
    padding: '0 16px',
    borderRadius: 14,
    border: '1px solid rgba(209,120,255,0.56)',
    fontSize: 16,
    outline: 'none',
    color: '#FFF6D8',
    background: 'rgba(5,1,18,0.52)',
    boxSizing: 'border-box',
    boxShadow: 'inset 0 0 18px rgba(0,0,0,0.18)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    appearance: 'none',
  },
  button: {
    width: '100%',
    minHeight: 62,
    marginTop: 26,
    borderRadius: '999px',
    border: '1px solid rgba(255,230,147,0.95)',
    background: 'linear-gradient(180deg, #FFE08B 0%, #D9902E 48%, #9B551B 100%)',
    color: '#1E1230',
    fontWeight: 900,
    fontSize: 'clamp(1rem, 3.5vw, 1.18rem)',
    cursor: 'pointer',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    boxShadow: '0 0 28px rgba(218,165,32,0.48), 0 0 22px rgba(205,69,255,0.42), inset 0 1px 0 rgba(255,255,255,0.55)',
    transition: 'transform 140ms ease, box-shadow 140ms ease, filter 140ms ease',
    WebkitTapHighlightColor: 'transparent',
  },
  btnSpark: {
    fontSize: '1.05rem',
    color: 'rgba(255,255,255,0.9)',
    textShadow: '0 0 10px rgba(255,255,255,0.8)',
  },
  securityNote: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 16,
    padding: '10px 12px',
    borderRadius: 14,
    background: 'rgba(18,4,38,0.58)',
    border: '1px solid rgba(255,224,139,0.28)',
    color: '#FFE08B',
    fontSize: 13,
    fontWeight: 800,
    textAlign: 'center',
    lineHeight: 1.35,
    boxShadow: '0 0 16px rgba(218,165,32,0.12)',
  },
  loginBox: {
    width: '100%',
    maxWidth: 520,
    marginTop: 10,
  },
  dividerRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    color: 'rgba(255,255,255,0.74)',
    marginBottom: 18,
  },
  dividerLine: {
    height: 1,
    flex: 1,
    background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.42), transparent)',
  },
  dividerText: {
    whiteSpace: 'nowrap',
    fontSize: '1rem',
  },
  loginButton: {
    width: 'min(100%, 510px)',
    minHeight: 58,
    margin: '0 auto',
    borderRadius: '999px',
    border: '1px solid rgba(232,95,255,0.82)',
    background: 'rgba(12,2,30,0.42)',
    color: '#FFE8A8',
    boxShadow: '0 0 24px rgba(196,69,255,0.36), inset 0 0 18px rgba(218,165,32,0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    fontSize: 'clamp(0.95rem, 3.4vw, 1.1rem)',
    fontWeight: 900,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  error: {
    background: 'linear-gradient(180deg, rgba(160,20,48,0.9), rgba(102,6,32,0.9))',
    color: 'white',
    padding: '12px 15px',
    borderRadius: 14,
    textAlign: 'center',
    marginBottom: 18,
    border: '1px solid rgba(255,255,255,0.14)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.22)',
  },
  success: {
    background: 'linear-gradient(180deg, rgba(31,140,80,0.92), rgba(14,90,54,0.92))',
    color: 'white',
    padding: '12px 15px',
    borderRadius: 14,
    textAlign: 'center',
    marginBottom: 18,
    border: '1px solid rgba(255,255,255,0.14)',
    boxShadow: '0 12px 24px rgba(0,0,0,0.22)',
  },
  smallError: {
    display: 'block',
    color: '#ffb4c0',
    marginTop: 8,
    fontWeight: 800,
  },
  toggleIcon: {
    position: 'absolute',
    right: 14,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 34,
    height: 34,
    borderRadius: '50%',
    border: 'none',
    background: 'transparent',
    color: '#FFD979',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
    display: 'grid',
    placeItems: 'center',
    padding: 0,
    textShadow: '0 0 12px rgba(218,165,32,0.35)',
  },
  bottomSigil: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 14,
    color: '#FFE09A',
    opacity: 0.78,
    marginTop: 14,
    filter: 'drop-shadow(0 0 12px rgba(218,165,32,0.45))',
  },
};

export default RegisterScreen;
